import type { LengthUnit, RawProfilePoint } from '@/types/profile';

/** Wood bat regulations put overall length in a well-known, narrow band per unit system. */
const MM_LENGTH_RANGE = { min: 500, max: 950 }; // ~20"-37"
const IN_LENGTH_RANGE = { min: 20, max: 37 };

/**
 * Detects whether a trimmed profile is expressed in millimeters or inches.
 *
 * Mark Lumber's lathes are configured to export millimeters, but the
 * importer must not assume that blindly — a mis-tagged file would silently
 * produce a bat 25x too small or too large. We infer units from the overall
 * length span, which is physically constrained for a baseball bat.
 */
export function detectUnit(points: RawProfilePoint[]): LengthUnit {
  if (points.length === 0) return 'mm';
  const positions = points.map((p) => p.position);
  const span = Math.max(...positions) - Math.min(...positions);

  const mmPlausible = span >= MM_LENGTH_RANGE.min && span <= MM_LENGTH_RANGE.max;
  const inPlausible = span >= IN_LENGTH_RANGE.min && span <= IN_LENGTH_RANGE.max;

  if (mmPlausible && !inPlausible) return 'mm';
  if (inPlausible && !mmPlausible) return 'in';

  // Ambiguous (rare, e.g. a very short blank) — default to mm, the shop standard.
  return 'mm';
}

export const MM_PER_INCH = 25.4;

export function convertLength(value: number, from: LengthUnit, to: LengthUnit): number {
  if (from === to) return value;
  return from === 'in' ? value * MM_PER_INCH : value / MM_PER_INCH;
}

export function formatLength(valueMm: number, unit: LengthUnit, decimals = 2): string {
  const value = unit === 'mm' ? valueMm : valueMm / MM_PER_INCH;
  return `${value.toFixed(decimals)} ${unit}`;
}
